import React, { useMemo } from 'react';

import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';

import Image, { TRANSFORMATIONS } from 'components/uiLibrary/Image';

import EntityStatus from 'components/EntityStatus';
import QuickFilters from 'components/Filters/Common/QuickFilters';
import AppliedFilters from 'components/Filters/Display/AppliedFilters';
import FilterFacets, { FILTER_LAYOUT } from 'components/Filters/Display/FilterFacets';
import { COMPONENTS, SECTIONS } from 'components/Globals/Analytics/constants';
import CastingToolPromo from 'components/Globals/CastingToolPromo';
import StickyHeader from 'components/Globals/Layout/Header/StickyHeader';
import PageWrapper from 'components/Globals/Layout/PageWrapper';
import { ThemeProvider, THEMES } from 'components/Globals/Layout/ThemeProvider';

import { TP, ENTITY_MAIN_TABS } from 'constants/index';

import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import useEntitySectionTabs from 'utils/hooks/useEntitySectionTabs';
import useOnScrollBeyondElement from 'utils/hooks/useOnScrollBeyondElement';
import usePageContext from 'utils/hooks/usePageContext';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES, ENTITY_VALIDATION_STATUS, FILTER_TYPES } from 'operabase-router/constants';

import Actions from './Actions';
import PageCommonContent from './PageCommonContent';
import PageH1 from './PageH1';
import classes from './PageLayout.module.scss';

/* NOTE: Using shared component from Globals */
const ExploreMoreWrapper = dynamic(() => import('components/Globals/Layout/StubPage/ExploreMore'));
const ArtistIndexPageFilters = dynamic(() => import('components/Artist/Display/IndexPageFilters'));
const AgenciesIndexPageFilters = dynamic(() => import('components/Manager/Display/IndexPageFilters'));
const PageNavigationTabs = dynamic(() => import('components/uiLibrary/PageNavigationTabs'));
const OrganizationIndexPageFilters = dynamic(() => import('components/Organization/Display/IndexPageFilters'), {
  ssr: false,
});

const INDEX_PAGE_FILTERS = {
  [ENTITY_TYPES.PROFILE]: ArtistIndexPageFilters,
  [ENTITY_TYPES.ORGANIZATION]: OrganizationIndexPageFilters,
  [ENTITY_TYPES.AGENCY]: AgenciesIndexPageFilters,
};

const H1Header = () => {
  const { isDesktop, isTablet } = useDeviceTypeLayouts();
  const { obRouteContext } = useAppContext();
  const { entity } = usePageContext();
  const { page } = obRouteContext;
  const { entityType } = page;

  const getTitleFontSize = () => {
    if (isDesktop) {
      return 26;
    }

    if (isTablet) {
      return 20;
    }

    return 18;
  };

  const titleFontSize = getTitleFontSize();

  return (
    <div className={classes.h1HeaderWrapper}>
      <div className={classes.h1Header}>
        {entity?.image?.medium && (
          <Image
            alt={entity.name}
            src={entity?.image?.medium}
            transformations={TRANSFORMATIONS.PROFILE_THUMBNAIL_LARGE}
            disableNextImage
            lazy={false}
            width={30}
            height={40}
            className={classes.profileImage}
          />
        )}
        <PageH1 maxFontSize={titleFontSize} minFontSize={titleFontSize} styles={{ title: classes.h1Header_title }} />
      </div>
      <div className={classes.actions}>
        <Actions
          entityType={entityType}
          entity={entity}
          trackingData={{
            section: SECTIONS.ENTITY_HEADER,
          }}
        />
      </div>
      {entityType === ENTITY_TYPES.PROFILE && entity?.id && (
        <div className={classes.castingToolPromo}>
          <CastingToolPromo
            entityType={entityType}
            entity={entity}
            asCTA
            trackingData={{
              section: SECTIONS.ENTITY_HEADER,
              component: COMPONENTS.VIEW_IN_CASTING_TOOL_CTA,
            }}
          />
        </div>
      )}
    </div>
  );
};

const PageHeader = ({ entityType, entity }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { obRouteContext } = useAppContext();
  const { isMobile } = useDeviceTypeLayouts();
  const { linkProps, page } = obRouteContext || {};
  const IndexPageFilters = INDEX_PAGE_FILTERS[entityType];
  const shouldShowIndexFilters = !page?.entity?.id && page?.entityType !== ENTITY_TYPES.AGENCY;
  const showContextualFilter =
    (!entityType || (entityType === ENTITY_TYPES.PRODUCTION && !entity?.id)) && !linkProps?.filters?.rentals;
  const { ref, isScrolledBeyond } = useOnScrollBeyondElement();

  // NOTE: Consolidate all filter showing logic here
  const shouldShowFilters =
    shouldShowIndexFilters &&
    (!page?.entity?.id || (!!page?.entity?.id && linkProps?.tab === ENTITY_MAIN_TABS.PERFORMANCES));

  return (
    <>
      <div className={classes.header} ref={ref}>
        <H1Header />
        {!page?.entity?.id && IndexPageFilters && (
          <div
            className={classnames({
              [classes.indexPageFilters]: !!shouldShowIndexFilters,
            })}
          >
            <IndexPageFilters />
          </div>
        )}
        {showContextualFilter && (
          <div className={classes.contextualFilter}>
            <section>
              <QuickFilters
                filterType={FILTER_TYPES.SEASON}
                limit={15}
                collapsable={!isMobile}
                scrollable={isMobile}
                includeSpecialOptions
                trackingData={{
                  section: SECTIONS.ENTITY_HEADER,
                  component: COMPONENTS.QUICK_FILTERS,
                }}
                variant="h3"
                showQuickViewFilterOption
                quickViewFilterOptionProps={{
                  name: t(`${TP}.YEARS`),
                  variant: 'h2',
                  startPlacement: true,
                  endPlacement: true,
                }}
              />
            </section>
          </div>
        )}
        <Filters
          entity={entity}
          entityType={entityType}
          showFilters={shouldShowFilters}
          trackingData={{
            section: SECTIONS.ENTITY_HEADER,
            component: COMPONENTS.FILTERS,
          }}
        />
      </div>
      <ThemeProvider theme={THEMES.LIGHT}>
        <StickyHeader isScrolledBeyond={isScrolledBeyond} />
      </ThemeProvider>
    </>
  );
};

const Filters = ({
  entity,
  entityType,
  hideAppliedFilters = false,
  className,
  showFilters = true,
  trackingData = {},
}) => {
  const excludeFilterTypes = useMemo(() => {
    if (!entity?.id) {
      return [FILTER_TYPES.WATCH_ONLINE];
    }

    return [];
  }, [entity?.id]);

  if (!showFilters) {
    return null;
  }

  return (
    <div
      className={classnames(classes.filtersFacetsWrapper, {
        [classes.filtersFacetsWrapper_filtersOnEntityPage]: !!entity?.id,
        [classes.filtersFacetsWrapper_withoutContextualFilter]: !!entityType,
        [className]: !!className,
      })}
    >
      <FilterFacets
        layout={FILTER_LAYOUT.HORIZONTAL}
        styles={{
          root: classes.filterContainerDetailed,
          filterGroupBox: classes.filterGroupBox,
        }}
        scrollable
        trackingData={trackingData}
      />
      {!hideAppliedFilters && (
        <AppliedFilters
          clearExcludedTypes={excludeFilterTypes}
          className={classes.appliedFiltersWrapper}
          trackingData={trackingData}
        />
      )}
    </div>
  );
};

const PageFooter = ({ entity }) => (
  <div className={classes.footer}>
    {/* NOTE: Contextual foot links are not shown on entity pages as they are part of base wrapper as entitySuggestionsFooter is part of it 
        which comes after ContextualFootLinks */}
    {!entity?.id && <ExploreMoreWrapper />}
  </div>
);

const NavigationTabs = ({ entityType, entity }) => {
  const { obRouteContext } = useAppContext();
  const { isMobile } = useDeviceTypeLayouts();
  const linkProps = obRouteContext?.linkProps;
  const isStubPageWithTabs = !!entity?.id && entityType !== ENTITY_TYPES.PRODUCTION;
  const tabs = useEntitySectionTabs({
    entityType,
    entity,
    mainPath: linkProps?.tab,
    filters: linkProps?.filters,
    baseRoute: obRouteContext?.basePath,
  });
  const activeTab = tabs?.find(({ key }) => key === linkProps?.tab) || tabs?.[0];
  const isPaidEntity = entity?.stats?.isPro || entity?.isPro;
  const showContextualFilter =
    (!linkProps?.tab || linkProps?.tab === ENTITY_MAIN_TABS.PERFORMANCES) &&
    !(entityType === ENTITY_TYPES.ORGANIZATION && !entity?.stats?.productions?.exists);
  const isOverviewTab = !linkProps?.tab;

  // NOTE: Consolidate filter showing logic - for NavigationTabs (stub pages), we don't need the entity?.id conditions
  const showInlineFilters = useMemo(() => {
    const isPerformanceTab = linkProps?.tab === ENTITY_MAIN_TABS.PERFORMANCES;
    const isValidEntityType = [ENTITY_TYPES.PROFILE, ENTITY_TYPES.ORGANIZATION].includes(entityType);

    // Hide filters for organizations without productions or performances
    const isOrganizationWithoutPerformances =
      entityType === ENTITY_TYPES.ORGANIZATION && !entity?.stats?.productions?.exists;

    if (isOrganizationWithoutPerformances) {
      return false;
    }

    return isValidEntityType && (isPerformanceTab || (isOverviewTab && !isPaidEntity));
  }, [entityType, isPaidEntity, linkProps?.tab, isOverviewTab, entity?.stats?.productions?.exists]);

  if (!isStubPageWithTabs) {
    return null;
  }

  return (
    <div
      className={classnames(classes.navigation, {
        [classes.navigation__isNonOverviewTab]: !showContextualFilter,
      })}
    >
      <PageNavigationTabs
        tabs={tabs}
        activeTab={activeTab}
        trackingData={{
          section: SECTIONS.ENTITY_HEADER,
        }}
      />
      {showContextualFilter && (
        <div className={classes.navigationContextualFilters}>
          <div className={classes.navigationContextualFilters_quickFilters}>
            <QuickFilters
              filterType={FILTER_TYPES.SEASON}
              limit={15}
              collapsable={!isMobile}
              scrollable={isMobile}
              skipBorder={isPaidEntity}
              includeSpecialOptions
              trackingData={{
                section: SECTIONS.ENTITY_HEADER,
                component: COMPONENTS.QUICK_FILTERS,
              }}
              withAppliedFilters={!isOverviewTab}
            />
          </div>
          <div className={classes.navigationContextualFilters_filterFacets}>
            {!showInlineFilters && isOverviewTab && (
              <FilterFacets
                layout={FILTER_LAYOUT.BUTTON}
                trackingData={{
                  section: SECTIONS.ENTITY_HEADER,
                }}
              />
            )}
          </div>
        </div>
      )}
      <Filters
        entity={entity}
        entityType={entityType}
        className={classes.stubPageFilters}
        hideAppliedFilters
        showFilters={showInlineFilters}
        trackingData={{
          section: SECTIONS.ENTITY_HEADER,
        }}
      />
    </div>
  );
};

/**
 * PageLayout component for standard page layouts
 */
const PageLayout = ({ children }) => {
  const { obRouteContext } = useAppContext();
  const { entity } = usePageContext();
  const { page, basePath } = obRouteContext;
  const { entityType } = page || {};
  const validationStatus = obRouteContext?.page?.entity?.validation;
  const isPaidEntity = entity?.stats?.isPro;
  // NOTE: Remove this once we have a integrated production stub page header in this file
  const isProductionStubPage = entityType === ENTITY_TYPES.PRODUCTION && !!entity?.id;

  const premiumThemeContext = useMemo(
    () => (ENTITY_TYPES.PROFILE === entityType && isPaidEntity ? THEMES.DARK : THEMES.LIGHT),
    [entityType, isPaidEntity],
  );

  const shouldShowEntityStatus = useMemo(
    () =>
      page?.entity?.id &&
      (!isEmpty(entity?.suspension) ||
        validationStatus === ENTITY_VALIDATION_STATUS.APPROVED_HIDDEN ||
        (!isPaidEntity && (entityType === ENTITY_TYPES.ORGANIZATION || entityType === ENTITY_TYPES.PROFILE))),
    [entity?.suspension, entityType, isPaidEntity, validationStatus, page?.entity?.id],
  );

  return (
    <PageWrapper>
      {shouldShowEntityStatus && (
        <EntityStatus
          entityInfo={entity}
          entityType={entityType}
          className={classes.entityStatus}
          validationStatus={validationStatus}
          isPaidEntity={isPaidEntity}
        />
      )}
      <ThemeProvider theme={premiumThemeContext}>
        {!isProductionStubPage && (
          <div className={classes.root}>
            <PageHeader entity={entity} entityType={entityType} />
            <NavigationTabs entity={entity} entityType={entityType} />
            <PageCommonContent />
          </div>
        )}
      </ThemeProvider>
      <ThemeProvider theme={THEMES.LIGHT}>
        <div
          className={classnames(classes.children, {
            [classes.children_isProductionStubPage]: isProductionStubPage, // NOTE: Remove this once we have a integrated production stub page header in this file
          })}
        >
          {children}
        </div>
      </ThemeProvider>
      <ThemeProvider theme={premiumThemeContext}>
        <PageFooter basePath={basePath} entity={entity} />
      </ThemeProvider>
    </PageWrapper>
  );
};

export default PageLayout;
